/**
 * Backend returns 400 with this shape when optimistic lock fails (version_id is not the latest).
 * Handle in UI: show friendly message and optionally refetch latest version.
 *
 * @format
 */

export const VERSION_CONFLICT_CODE = "VERSION_CONFLICT" as const;

export interface VersionConflictBody {
  code: typeof VERSION_CONFLICT_CODE;
  latest_version_id?: string;
  latest_version_num?: number;
  error?: string;
}

const LEGACY_MESSAGE = "Provided version is not the latest";

export function isVersionConflict(
  error: unknown,
): error is { response: { data: VersionConflictBody } } {
  const data = (error as { response?: { data?: unknown } })?.response?.data;
  if (!data || typeof data !== "object") return false;
  const body = data as VersionConflictBody & { error?: string };
  if (body.code === VERSION_CONFLICT_CODE) return true;
  const msg = (body.error ?? "").toLowerCase();
  return (
    msg.includes(LEGACY_MESSAGE.toLowerCase()) ||
    msg.includes("updated elsewhere")
  );
}

export function getVersionConflictBody(
  error: unknown,
): VersionConflictBody | null {
  if (!isVersionConflict(error)) return null;
  return error.response.data;
}

export const VERSION_CONFLICT_MESSAGE =
  "This SOP was changed elsewhere. We reloaded the latest version.";

/** 403 when editing a version that is not draft (done/archived). */
export const VERSION_LOCKED_MESSAGE =
  "Version is locked (status: done). Cannot edit.";

export function isVersionLockedError(error: unknown): boolean {
  const status = (error as { response?: { status?: number } })?.response?.status;
  if (status !== 403) return false;
  const data = (error as { response?: { data?: { message?: string } } })
    ?.response?.data;
  const msg = (data?.message ?? "").toLowerCase();
  return (
    msg.includes("locked") ||
    msg.includes("cannot edit") ||
    msg.includes("status: done") ||
    msg.includes("status: archived")
  );
}

export function getVersionLockedMessage(error: unknown): string {
  const data = (error as { response?: { data?: { message?: string } } })
    ?.response?.data;
  return data?.message?.trim() || VERSION_LOCKED_MESSAGE;
}

export const PERMISSION_DENIED_MESSAGE =
  "You don't have permission to access this SOP.";

export function isPermissionDeniedError(error: unknown): boolean {
  if (isVersionLockedError(error)) return false;
  const status = (error as { response?: { status?: number } })?.response?.status;
  return status === 403;
}

export function getPermissionDeniedMessage(error: unknown): string {
  if (!isPermissionDeniedError(error)) return PERMISSION_DENIED_MESSAGE;
  const data = (error as { response?: { data?: { message?: string } } })
    ?.response?.data;
  return data?.message?.trim() || PERMISSION_DENIED_MESSAGE;
}
